import { useId, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface DetailTab {
  key: string
  label: string
  /** Shown after the label — an open count, a problem count. */
  badge?: number
  content: ReactNode
  disabled?: boolean
}

export interface DetailTabsProps {
  tabs: DetailTab[]
  /** Uncontrolled starting tab; omit for the first enabled one. */
  defaultTab?: string
  className?: string
}

/**
 * Tabbed sections on a detail screen.
 *
 * <p>Implements the ARIA tabs pattern rather than a row of buttons: arrow keys move between
 * tabs, Home and End jump to the ends, and only the selected tab is in the tab order. A
 * screen with six sections behind plain buttons costs a keyboard user six tab stops to reach
 * the content.
 *
 * <p>Inactive panels are unmounted, not hidden. A hidden panel's inputs stay focusable and
 * submittable, which is how a form ends up sending a field nobody could see.
 */
export function DetailTabs({ tabs, defaultTab, className }: DetailTabsProps) {
  const baseId = useId()
  const enabled = tabs.filter((tab) => !tab.disabled)
  const [active, setActive] = useState(defaultTab ?? enabled[0]?.key ?? '')

  const current = tabs.find((tab) => tab.key === active) ?? enabled[0]

  function move(direction: 1 | -1) {
    const index = enabled.findIndex((tab) => tab.key === active)
    const next = enabled[(index + direction + enabled.length) % enabled.length]
    if (next) setActive(next.key)
  }

  return (
    <div className={className}>
      <div
        role="tablist"
        className="flex gap-1 overflow-x-auto overscroll-x-contain border-b border-[var(--color-border)]"
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') { event.preventDefault(); move(1) }
          if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1) }
          if (event.key === 'Home') { event.preventDefault(); setActive(enabled[0]?.key ?? active) }
          if (event.key === 'End') { event.preventDefault(); setActive(enabled.at(-1)?.key ?? active) }
        }}
      >
        {tabs.map((tab) => {
          const selected = tab.key === current?.key
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={`${baseId}-tab-${tab.key}`}
              aria-controls={`${baseId}-panel-${tab.key}`}
              aria-selected={selected}
              // Only the selected tab is reachable by Tab; the arrows move within the list.
              tabIndex={selected ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => setActive(tab.key)}
              className={cn(
                '-mb-px flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selected
                  ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                  : 'border-transparent text-[var(--color-body)] hover:text-[var(--color-heading)]',
              )}
            >
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span className="rounded-full bg-[var(--color-primary-soft)] px-2 py-0.5 text-xs text-[var(--color-primary)]">
                  {tab.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {current && (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${current.key}`}
          aria-labelledby={`${baseId}-tab-${current.key}`}
          tabIndex={0}
          className="pt-4"
        >
          {current.content}
        </div>
      )}
    </div>
  )
}
